import React, { useRef } from "react";
import { getPerformanceBadgeClass } from "../dpatScoreStats";
import DpatExportButtons from "./DpatExportButtons";
import {
  buildRegionRankingHtml,
  buildRegionRankingRows,
  downloadCsvFromRows,
  downloadExcelSheets,
  downloadNodeAsPdf,
  downloadWordFromHtml,
} from "../dpatDashboardExport";

const RegionRankingTable = ({ rows, loading, reportingYear, tableCaptureRef }) => {
  const localRef = useRef(null);
  const captureRef = tableCaptureRef || localRef;

  const handleTablePdf = async () => {
    if (!captureRef.current) return;
    await downloadNodeAsPdf(captureRef.current, `dpat-region-ranking-${reportingYear}.pdf`);
  };

  const handleTableWord = () => {
    downloadWordFromHtml(
      buildRegionRankingHtml(rows, reportingYear),
      `dpat-region-ranking-${reportingYear}.doc`,
    );
  };

  const handleTableExcel = () => {
    downloadExcelSheets(
      [{ name: "Region ranking", rows: buildRegionRankingRows(rows) }],
      `dpat-region-ranking-${reportingYear}.xlsx`,
    );
  };

  const handleTableCsv = () => {
    downloadCsvFromRows(
      buildRegionRankingRows(rows),
      `dpat-region-ranking-${reportingYear}.csv`,
    );
  };

  return (
  <section ref={captureRef} className="rounded-xl border border-border bg-card shadow-sm">
    <div className="border-b border-border px-4 py-4 md:px-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground md:text-xl">National region ranking</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Regions ranked by average district Final Outcome % for {reportingYear}
          </p>
        </div>
        <DpatExportButtons
          label="Download table"
          onPdf={handleTablePdf}
          onWord={handleTableWord}
          onExcel={handleTableExcel}
          onCsv={handleTableCsv}
          disabled={loading || rows.length === 0}
        />
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-left text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-4 py-3 font-semibold md:px-6">Rank</th>
            <th className="px-4 py-3 font-semibold md:px-6">Region</th>
            <th className="px-4 py-3 font-semibold md:px-6">Avg Final Outcome %</th>
            <th className="px-4 py-3 font-semibold md:px-6">Classification</th>
            <th className="px-4 py-3 font-semibold md:px-6">Districts Assemblies Assessed</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground md:px-6">
                Loading…
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground md:px-6">
                No regional averages available yet.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.regionKey} className="border-b border-border/60 hover:bg-muted/20">
                <td className="px-4 py-3 font-bold tabular-nums md:px-6">{row.rank}</td>
                <td className="px-4 py-3 font-medium text-foreground md:px-6">{row.regionName}</td>
                <td className="px-4 py-3 font-semibold tabular-nums md:px-6">
                  {row.regionAveragePercent.toFixed(1)}%
                </td>
                <td className="px-4 py-3 md:px-6">
                  <span
                    className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${getPerformanceBadgeClass(row.classification)}`}
                  >
                    {row.classification}
                  </span>
                </td>
                <td className="px-4 py-3 tabular-nums text-muted-foreground md:px-6">
                  {row.assessedCount} / {row.districtCount}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </section>
  );
};

export default RegionRankingTable;
